/**
 * Die Übergabe an uPlot (HM-054).
 *
 * **Warum eine Bibliothek und nicht weiter selbstgezeichnetes SVG** (ADR 0021):
 * Ein Verlauf über 30 Tage im Fünf-Minuten-Raster sind 8.640 Punkte je Reihe.
 * Als SVG wären das ebenso viele Segmente im DOM, und ein Fadenkreuz müsste bei
 * jeder Mausbewegung dagegen rechnen. uPlot zeichnet auf Canvas, wiegt 45 kB,
 * hängt an nichts und steht unter MIT.
 *
 * **Diese Datei ist die einzige, die uPlot kennt.** Alles Fachliche steht in
 * `frame.ts`, `align.ts`, `stack.ts` und `range.ts` und ist ohne Browser
 * geprüft. Wer die Bibliothek eines Tages tauscht, tauscht diese Datei und die
 * beiden Zusatzstücke daneben.
 *
 * **Alles Veränderliche wird bei jedem Zeichnen gelesen**, nicht beim Aufbau:
 * Zeitfenster, Zahlenformat und Marken kommen über `state()` herein. Sonst
 * müsste das Diagramm bei jedem Nachladen neu gebaut werden — und ein
 * Ablesefenster, das dabei zuklappt, macht die Seite unbenutzbar.
 */
import uPlot from "uplot";
import { DEFAULT_LOCALE, formatInstant } from "@hausmeter/i18n";
import { inferStep } from "./align.js";
import { barExtent } from "./bars.js";
import {
  bucketLabel,
  spanAt,
  spanEnd,
  timeTicks,
  type ChartBucket,
  type TimeTick,
} from "./buckets.js";
import { decorationsPlugin } from "./decorations.js";
import { hoverPlugin } from "./hover.js";
import { buildFrame, stepOf, type ChartFrame } from "./frame.js";
import type { ChartPalette } from "./palette.js";
import { hasAlpha, withAlpha } from "./palette.js";
import { padRange, zeroBasedTicks } from "./range.js";
import { tooltipPlugin } from "./tooltip.js";
import type { ChartProps } from "./types.js";

/**
 * Die Schriftgröße der Achsen — **eine ganze Zahl, sonst wird sie falsch**.
 * uPlot rechnet sie mit `/(\d+)px/` auf die Pixeldichte hoch (`pxRatioFont`);
 * aus „10.5px" trifft das nur „5px", und bei Dichte 2,6 stand im Canvas
 * „10.13px" — auf jedem Telefon Achsenschrift von vier Bildpunkten, am
 * Monitor mit Dichte 1 unsichtbar.
 */
const AXIS_FONT_PX = 11;

/** Unter dieser Abschnittsbreite (CSS-Bildpunkte) rücken die Balken enger zusammen. */
const NARROW_SPAN_PX = 40;

/** Wie durchscheinend eine Fläche unter einer einzelnen Linie liegt. */
const AREA_ALPHA = 0.12;
/**
 * Gestapelte Flächen sind fast deckend: Zwei durchscheinende Flächen
 * übereinander ergeben eine dritte Farbe, die in keiner Legende steht.
 */
const STACK_ALPHA = 0.85;
/** Das Band liegt unter der Linie und ist bewusst blass — es ist keine Messreihe. */
const BAND_ALPHA = 0.16;
/**
 * Wie weit die übrigen Balken beim Überfahren zurücktreten (HM-142, Entwurf: auf
 * 40 % Deckkraft) — als Schleier in der Grundfarbe, wie bei einer Teilsumme.
 */
const HOVER_DIM_ALPHA = 0.6;
/** Die Linienstärke der Reihen, wie im Entwurf. */
const LINE_WIDTH = 2.5;
/** Wie hoch die Leiste unter einem Abschnitt mit Ersatzwert ist, in CSS-Pixeln (HM-115). */
const SUBSTITUTE_STRIP_PX = 3;
/**
 * Wie stark der Grund über einem Balken mit Teilsumme liegt (HM-105). Blass
 * genug, dass man den Unterschied sieht; deckend genug, dass die Farbe der
 * Reihe bleibt.
 */
const PARTIAL_VEIL_ALPHA = 0.55;

/** Der Name der Skala der rechten Achse. */
const RIGHT = "r";

export function buildOptions(
  frame: ChartFrame,
  palette: ChartPalette,
  state: () => ChartProps,
): uPlot.Options {
  const initial = state();
  const hasRight = frame.slots.some((entry) => entry.axis === "right");
  const locale = initial.locale ?? DEFAULT_LOCALE;

  /**
   * Wie lang ein Abschnitt ist — aus dem Raster, sonst aus der Nennweite,
   * sonst geschätzt (HM-099). Gelesen beim Zeichnen, denn das Fenster und mit
   * ihm das Raster wechseln mit jeder Navigation.
   */
  /** Der Schritt der linken Achse, wenn sie an einer beschrifteten Linie endet (Balken). */
  let yStep: number | null = null;

  const geometry = (times: readonly (number | null)[]) => {
    const current = state();
    const known = times.filter((at): at is number => at !== null);
    return {
      bucket: current.bucket ?? null,
      step: stepOf(current) ?? inferStep(known) ?? 1,
      timeZone: current.timeZone,
    };
  };

  /**
   * Die Marken der Zeitachse werden gemerkt, solange Fenster, Zeitzone, Raster
   * und Breite gleich bleiben: Sie kosten je Tag einen Blick in die Zeitzone,
   * und die Achse fragt bei jedem Zeichnen erneut.
   */
  let plan: { key: string; ticks: readonly TimeTick[]; labels: Map<number, string> } | null = null;
  const ticksFor = (u: uPlot) => {
    const current = state();
    const width = Math.round(u.bbox.width / uPlot.pxRatio);
    const centered: ChartBucket | null = frame.bucketed ? (current.bucket ?? null) : null;
    const key = [current.from, current.to, current.timeZone, centered ?? "", width].join("|");
    if (plan === null || plan.key !== key) {
      const ticks = timeTicks({
        from: current.from,
        to: current.to,
        timeZone: current.timeZone,
        locale,
        width,
        centered,
      });
      plan = { key, ticks, labels: new Map(ticks.map((tick) => [tick.at, tick.label])) };
    }
    return plan;
  };

  /**
   * Die Überschrift des Ablesefensters. Ein Balken oder ein Stunden-, Tages-
   * und Monatsmittel ist ein Abschnitt und heißt so („14:00 – 15:00",
   * „Mittwoch, 2. September 2026"); ein Momentanwert im Minutenraster bleibt
   * ein Zeitpunkt.
   */
  const timeLabel = (at: number): string => {
    const current = state();
    const bucket = current.bucket;
    if (
      bucket !== undefined &&
      (frame.bucketed || bucket === "1h" || bucket === "1d" || bucket === "1w" || bucket === "1M")
    ) {
      return bucketLabel(at, bucket, current.timeZone, locale);
    }
    return formatInstant(new Date(at), current.timeZone, locale, "datetime");
  };

  /**
   * Die Matrix zum **jüngsten** Stand der Werte. Der Aufbau hängt an `frame`,
   * aber neue Werte kommen über `setData` — und mit ihnen neue Status. Was pro
   * Zeitpunkt gelesen wird (Ablesefenster, Ersatzwert-Leiste), liest deshalb
   * hier; gemerkt, solange sich die Eingabe nicht ändert.
   */
  let latestFrame: { readonly props: ChartProps; readonly frame: ChartFrame } | null = null;
  const currentFrame = (): ChartFrame => {
    const props = state();
    if (latestFrame === null || latestFrame.props !== props) {
      latestFrame = { props, frame: buildFrame(props, palette) };
    }
    return latestFrame.frame;
  };

  const series: uPlot.Series[] = [{}];
  const bands: uPlot.Band[] = [];

  /**
   * Ungestapelte Balken stehen **nebeneinander** in ihrem Abschnitt, gestapelte
   * übereinander (HM-099). Zwei Reihen auf derselben Stelle zeichneten die
   * größere über die kleinere — „Verbrauch und Erzeugung" zeigte an jedem
   * Sommertag nur die Erzeugung, und der Verbrauch war nicht verschwunden,
   * sondern verdeckt.
   */
  const barSlots = frame.slots.filter((entry) => entry.mark === "bars").map((entry) => entry.slot);
  const lanes = frame.stacked ? 1 : barSlots.length;
  /**
   * Die Balken mit Teilsumme je Reihe, gesammelt beim Bauen der Pfade und
   * direkt nach dem Zeichnen der Reihe blasser gelegt (HM-105). Ein Pfad je
   * Reihe und nicht einer für alle: Im Stapel deckt die Reihe darunter die
   * obere, und ihr Balken bekommt seinen eigenen Schleier — oder keinen.
   */
  const partial = new Map<number, Path2D>();
  /**
   * Die Balken je Reihe und Index, gesammelt beim Bauen der Pfade — beim
   * Überfahren treten alle außer denen des Abschnitts unter dem Zeiger zurück
   * (HM-142). Neu gezeichnet wird dafür, nicht neu gebaut.
   */
  const barRects = new Map<number, Map<number, readonly [number, number, number, number]>>();
  let hovered: number | null = null;
  const hasLine = frame.slots.some((entry) => entry.mark !== "bars");

  for (const band of frame.bands) {
    bands.push({ series: [band.high, band.low], fill: withAlpha(band.color, BAND_ALPHA) });
  }

  for (let slot = 1; slot < frame.data.length; slot += 1) {
    const entry = frame.slots.find((candidate) => candidate.slot === slot);

    // Was keine Reihe ist, ist eine Bandkante: Sie trägt Werte, damit die
    // Fläche dazwischen entsteht, und wird selbst nicht gezeichnet.
    if (entry === undefined) {
      series.push({ stroke: "transparent", width: 0, points: { show: false } });
      continue;
    }

    /* Die Linie der rechten Achse (HM-137): eigene Skala, gestrichelt, und über
       Balken mittig im Abschnitt — ihr Wert gilt dem ganzen Abschnitt wie der
       Balken darunter, nicht seinem Beginn. */
    if (entry.axis === "right") {
      series.push({
        label: entry.label,
        scale: RIGHT,
        stroke: entry.color,
        width: LINE_WIDTH,
        ...(entry.dashed ? { dash: [5, 4] } : {}),
        paths: frame.bucketed ? centeredLine(geometry) : undefined,
        // Der Punkt des Fadenkreuzes säße am Beginn des Abschnitts, die Linie in seiner Mitte.
        points: { show: !frame.bucketed },
      });
      continue;
    }

    series.push({
      label: entry.label,
      /**
       * Ein Balken bekommt einen Rand in der Grundfarbe statt einer eigenen
       * Linie: Er ist die zwei Bildpunkte Luft zwischen zwei Segmenten eines
       * Stapels. Ohne ihn gehen Netzbezug und Eigenverbrauch ineinander über,
       * und die Grenze dazwischen ist die Aussage.
       */
      stroke: entry.mark === "bars" ? palette.surface : entry.color,
      width: entry.mark === "bars" ? 2 : LINE_WIDTH,
      fill:
        entry.mark === "bars"
          ? entry.color
          : entry.mark === "area"
            ? withAlpha(entry.color, frame.stacked ? STACK_ALPHA : AREA_ALPHA)
            : undefined,
      paths:
        entry.mark === "bars"
          ? spanBars(geometry, {
              lane: frame.stacked ? 0 : barSlots.indexOf(slot),
              lanes,
              // Die Fuge trennt ein Segment von dem darüber — das oberste hat
              // keines, und ohne Stapel gibt es nichts zu trennen.
              divider: frame.stacked && slot !== frame.total,
              coverage: entry.coverage,
              partial: (path) => partial.set(slot, path),
              rects: (rects) => barRects.set(slot, rects),
              // Die Kappe ist rund, wo nichts mehr darüber liegt (Entwurf).
              cap: !frame.stacked || slot === frame.total,
            })
          : undefined,
      points: entry.mark === "bars" ? { show: false } : undefined,
    });
  }

  return {
    // Die wirklichen Maße setzt die Komponente, sobald sie ihren Platz kennt.
    width: 600,
    height: initial.height ?? 220,
    /**
     * Millisekunden statt Sekunden: Der Vertrag rechnet durchgehend in ms, und
     * eine Umrechnung an einer Stelle ist eine, die irgendwann an einer zweiten
     * fehlt.
     */
    ms: 1,
    tzDate: (timestamp) => uPlot.tzDate(new Date(timestamp), state().timeZone),
    series,
    bands,
    // Die Legende ist eine eigene Komponente: Die eingebaute bringt ihr eigenes
    // Aussehen mit und stünde neben dem Design-System.
    legend: { show: false },
    /**
     * Oben Platz für die Beschriftung einer Marke, rechts für die **halbe**
     * letzte Zeitangabe: Sie steht mittig unter der rechten Kante, und ohne
     * den Rand schnitt der Canvas sie zu „14:0" ab. Mit rechter Achse steht
     * sie über deren Fläche und braucht keinen eigenen Rand — sonst lägen dort
     * Rand und Achse nebeneinander, auf einem Telefon ein Fünftel der Breite.
     */
    padding: [14, hasRight ? 0 : 26, 0, 0],
    scales: {
      /**
       * **Die Achse gehört dem abgefragten Fenster, nicht den Daten.** Ein
       * Gerät, das erst seit einer Stunde meldet, steht rechts im Bild statt
       * die Breite zu füllen — sonst zeigte jede Reihe „ihre" Zeit und zwei
       * Diagramme nebeneinander wären nicht vergleichbar.
       */
      x: {
        time: true,
        range: () => {
          const current = state();
          return [current.from, current.to];
        },
      },
      y: {
        range: (_, low, high) => {
          /* Beginnt die Achse bei null — Balken immer, Linien ohne negative
             Werte —, endet sie an einer beschrifteten Linie (`zeroBasedTicks`).
             Eine Spannung von 228 bis 232 V beginnt nicht bei null und behält
             ihren engen Ausschnitt. */
          const padded = padRange(low, high, { baseline: frame.baseline });
          const nice =
            padded[0] === 0 && (low ?? 0) >= 0
              ? zeroBasedTicks(high, { headroom: frame.baseline === "zero" ? 0.05 : 0 })
              : null;
          yStep = nice?.step ?? null;
          return nice !== null ? [0, nice.max] : padded;
        },
      },
      /* Fest, nie aus den Daten — das ist die Bedingung der rechten Achse (ADR 0021, Nachtrag). */
      ...(hasRight
        ? {
            [RIGHT]: {
              range: (): uPlot.Range.MinMax => {
                const range = state().rightRange ?? [0, 1];
                return [range[0], range[1]];
              },
            },
          }
        : {}),
    },
    axes: [
      {
        stroke: palette.text,
        grid: { show: false },
        ticks: { show: false },
        font: `${AXIS_FONT_PX}px ${palette.font}`,
        /**
         * Die Marken setzt `timeTicks`, nicht die Bibliothek: Sie kennt weder
         * lokale Tage noch die Mitte eines Balkens (HM-099). Eine Marke ohne
         * Beschriftung gibt es nicht — jede kommt aus demselben Plan.
         */
        /* So hoch wie eine Zeile Uhrzeit samt Abstand — uPlots Vorgabe von 50
           ließ unter den Marken einen leeren Streifen, auf einem Telefon ein
           Achtel der Diagrammhöhe. Der Abstand nach oben ist etwas größer als
           uPlots 5: Die Null der Wertachsen steht mittig auf der Grundlinie,
           und „24:00" rechts unten stieß sonst an die Null darüber. */
        gap: 9,
        size: AXIS_FONT_PX + 16,
        splits: (u) => ticksFor(u).ticks.map((tick) => tick.at),
        values: (u, splits) => splits.map((at) => ticksFor(u).labels.get(at) ?? ""),
      },
      {
        stroke: palette.text,
        /* Gestrichelt wie im Entwurf — sie sind Hilfslinien, kein Inhalt. */
        grid: { stroke: palette.grid, width: 1, dash: [3, 4] },
        ticks: { show: false },
        font: `${AXIS_FONT_PX}px ${palette.font}`,
        values: (_, splits) => splits.map((value) => state().format.axis(value)),
        /* Die Marken zum Schritt der Skala — sonst wählte uPlot eigene, und die
           oberste Linie läge wieder unter der Obergrenze. */
        splits: (_u: uPlot, _axis: number, min: number, max: number, found: number) => {
          const step = yStep ?? found;
          const marks: number[] = [];
          for (let value = Math.ceil(min / step) * step; value <= max + step * 1e-9; value += step) {
            marks.push(Math.round(value / step) * step);
          }
          return marks;
        },
        /**
         * **Die Achse ist so breit wie ihre breiteste Zahl.**
         *
         * Bei fester Breite lief die Beschriftung eines Zählerstands
         * (`11.049,2`) links aus dem Bild — und abgeschnitten wurde aus
         * elftausend tausend. Das ist der Fehler, den HM-053 im Betrieb
         * gefunden hat, und er kommt bei jeder festen Breite zurück.
         */
        size: (self, marks) => {
          if (marks === null || marks.length === 0) return 52;
          self.ctx.save();
          self.ctx.font = `${AXIS_FONT_PX * uPlot.pxRatio}px ${palette.font}`;
          const widest = Math.max(...marks.map((mark) => self.ctx.measureText(mark).width));
          self.ctx.restore();
          return widest / uPlot.pxRatio + 16;
        },
      },
      ...(hasRight
        ? [
            {
              scale: RIGHT,
              side: 1,
              stroke: palette.text,
              // Das Gitter gehört der linken Achse; zwei Gitter übereinander wären ein Muster.
              grid: { show: false },
              ticks: { show: false },
              font: `${AXIS_FONT_PX}px ${palette.font}`,
              values: (_: uPlot, splits: number[]) =>
                splits.map((value) => (state().rightFormat ?? state().format).axis(value)),
              /* So breit wie die breiteste Beschriftung, wie links — nicht fest. */
              size: (self: uPlot, marks: string[] | null) => {
                if (marks === null || marks.length === 0) return 36;
                self.ctx.save();
                self.ctx.font = `${AXIS_FONT_PX * uPlot.pxRatio}px ${palette.font}`;
                const widest = Math.max(...marks.map((mark) => self.ctx.measureText(mark).width));
                self.ctx.restore();
                /* Abstand zur Fläche (uPlots `gap`, 5) und ein Bildpunkt Luft —
                   rechts davon ist nur noch der Rand der Karte. */
                return widest / uPlot.pxRatio + 6;
              },
            } satisfies uPlot.Axis,
          ]
        : []),
    ],
    cursor: {
      /* Über Abschnitten steht die senkrechte Linie in ihrer Mitte (`hover.ts`);
         uPlots eigene folgte dem Zeiger und stünde neben den Punkten. */
      x: !frame.bucketed,
      y: false,
      /**
       * Kein Ziehen zum Vergrößern. Der Zeitraum steht in der Adresse (HM-037,
       * HM-053); ein Ausschnitt, den nur das Diagramm kennt, wäre nicht
       * verlinkbar und nach einem Neuladen weg.
       */
      drag: { x: false, y: false },
      points: {
        /* Ein Ring mit hellem Kern auf der Linie, wie im Entwurf; über Balken keiner. */
        size: 9,
        width: LINE_WIDTH,
        fill: palette.surface,
        /**
         * Wo der Punkt des Fadenkreuzes sitzt — dort, wo die Reihe gezeichnet ist.
         * Für die Linie der rechten Achse über Balken ist das die Mitte des
         * Abschnitts (HM-137); sonst säße der Punkt neben der Linie.
         */
        bbox: (u, seriesIdx) => {
          const half = 4;
          const hidden = { left: -half * 4, top: -half * 4, width: 0, height: 0 };
          const index = u.cursor.idx;
          const times = u.data[0] as readonly number[];
          const value = index === null || index === undefined ? undefined : u.data[seriesIdx]?.[index];
          if (index === null || index === undefined || value === null || value === undefined) return hidden;
          /* Über Balken gilt der Abschnitt unter dem Zeiger, wie im Ablesefenster:
             Steht er über einem leeren Monat, gibt es keinen Punkt — auch nicht am
             nächstgelegenen Wert. */
          if (frame.bucketed) {
            const left = u.cursor.left ?? -1;
            if (left < 0 || spanAt(times, u.posToVal(left, "x"), geometry(times)) !== index) return hidden;
          }
          const series = u.series[seriesIdx]!;
          if (frame.slots.find((entry) => entry.slot === seriesIdx)?.mark === "bars") return hidden;
          const centered = frame.bucketed && series.scale === RIGHT;
          const at = centered ? (times[index]! + spanEnd(times, index, geometry(times))) / 2 : times[index]!;
          return {
            left: u.valToPos(at, "x") - half,
            top: u.valToPos(value, series.scale ?? "y") - half,
            width: half * 2,
            height: half * 2,
          };
        },
      },
    },
    hooks: {
      /**
       * Ein Balken mit Teilsumme wird nach dem Zeichnen seiner Reihe mit dem
       * Grund überschleiert (HM-105): dieselbe Farbe, blasser. Eine zweite
       * Farbe wäre eine Reihe, die in keiner Legende steht; eine Schraffur
       * ist an einem sechs Bildpunkte breiten Viertelstundenbalken nicht zu
       * erkennen. Hier und nicht in einem Plugin, weil es zwischen zwei Reihen
       * eines Stapels geschehen muss.
       */
      drawSeries: [
        (u, seriesIdx) => {
          /* Die übrigen Balken treten beim Überfahren zurück (HM-142). */
          const rects = barRects.get(seriesIdx);
          if (hovered !== null && rects !== undefined && hasAlpha(palette.surface)) {
            u.ctx.save();
            u.ctx.fillStyle = withAlpha(palette.surface, HOVER_DIM_ALPHA);
            for (const [index, rect] of rects) {
              if (index !== hovered) u.ctx.fillRect(...rect);
            }
            u.ctx.restore();
          }
        },
        (u, seriesIdx) => {
          const path = partial.get(seriesIdx);
          // Ein Schleier, der nicht durchscheinend wäre, ist ein Kasten, der
          // den Balken verdeckt — dann lieber keiner (HM-106).
          if (path === undefined || !hasAlpha(palette.surface)) return;
          u.ctx.save();
          u.ctx.fillStyle = withAlpha(palette.surface, PARTIAL_VEIL_ALPHA);
          u.ctx.fill(path);
          u.ctx.restore();
        },
      ],
      /**
       * **Ein Ersatzwert bekommt eine Leiste unter seinem Abschnitt** (HM-115),
       * direkt unter der Grundlinie. Nicht die Balken selbst umgefärbt: Der
       * Schleier sagt schon „Teilsumme", eine Schraffur ist an einem
       * Viertelstundenbalken nicht zu erkennen, und eine zweite Farbe wäre eine
       * Reihe, die in keiner Legende steht. Die Leiste gilt dem Abschnitt und
       * trägt deshalb auch unter einer Linie — und sie verdeckt keinen Wert.
       */
      draw: [
        (u) => {
          const column = currentFrame().substituted;
          if (!column.some((quality) => quality !== null)) return;
          const times = u.data[0] as readonly number[];
          const shape = geometry(times);
          const ratio = uPlot.pxRatio;
          const right = u.bbox.left + u.bbox.width;
          const top = u.bbox.top + u.bbox.height + ratio;

          u.ctx.save();
          u.ctx.fillStyle = palette.substitute;
          for (let index = 0; index < times.length && index < column.length; index += 1) {
            if (column[index] === null || column[index] === undefined) continue;
            const x0 = u.valToPos(times[index]!, "x", true);
            const x1 = Math.min(u.valToPos(spanEnd(times, index, shape), "x", true), right);
            if (x1 <= u.bbox.left || x0 >= right) continue;
            u.ctx.fillRect(Math.max(x0, u.bbox.left), top, Math.max(x1 - Math.max(x0, u.bbox.left), ratio), SUBSTITUTE_STRIP_PX * ratio);
          }
          u.ctx.restore();
        },
      ],
    },
    plugins: [
      hoverPlugin(
        () => ({
          bucketed: frame.bucketed,
          lines: hasLine,
          locate: (at, times) => spanAt(times, at, geometry(times)),
          spanEnd: (times, index) => spanEnd(times, index, geometry(times)),
        }),
        (u, index) => {
          if (!frame.bucketed) return;
          hovered = index;
          u.redraw(false);
        },
      ),
      tooltipPlugin(() => {
        const current = state();
        return {
          // Abdeckung und Status je Zeitpunkt aus dem jüngsten Stand, nicht vom Aufbau.
          rows: currentFrame().rows,
          /**
           * Die Höhe des Stapels als eigene Zeile — sie stand vorher an der
           * obersten Reihe und trug damit deren Namen. Wie sie heißt, weiß nur
           * der Aufrufer: Bei einer Wohnung sind PV-Anteil und Netzanteil
           * zusammen der **Verbrauch**, und „Zusammen" ist der Rückfall, wo er
           * die Größe nicht benennt.
           */
          total:
            frame.total === null
              ? null
              : { slot: frame.total, label: current.stackTotalLabel ?? "Zusammen" },
          time: timeLabel,
          value: (value) => current.format.value(value),
          rightValue: (value) => (current.rightFormat ?? current.format).value(value),
          /**
           * Über Balken zählt der Abschnitt, nicht der nächste Zeitpunkt: Der
           * Zeiger auf der rechten Hälfte eines Balkens meint diesen Balken
           * und nicht seinen Nachbarn (HM-099).
           */
          locate: frame.bucketed ? (at, times) => spanAt(times, at, geometry(times)) : undefined,
        };
      }),
      decorationsPlugin(() => {
        const current = state();
        const single = frame.rows.length === 1 ? frame.rows[0]! : null;
        return {
          annotations: current.annotations ?? [],
          palette,
          /**
           * Der Endpunkt sagt „das ist der letzte Stand". Bei mehreren Reihen
           * wären es mehrere Punkte, die einander die Aussage nehmen; an einem
           * Balken säße er in der Luft.
           */
          endpoint:
            single !== null && single.mark !== "bars"
              ? { seriesIdx: single.slot, color: single.color }
              : null,
        };
      }),
    ],
  };
}

/**
 * Eine Linie durch die **Mitten** der Abschnitte (HM-137) — für die rechte Achse
 * über Balken. Ein Wert je Abschnitt gilt dem Abschnitt; an seinem Beginn
 * gezeichnet, stünde der Ladezustand von 14–15 Uhr über der Kante zu 13 Uhr.
 * Eine Lücke (`null`) unterbricht die Linie (Regel 8).
 */
function centeredLine(
  geometry: (times: readonly (number | null)[]) => {
    readonly bucket: ChartBucket | null;
    readonly step: number;
    readonly timeZone: string;
  },
): uPlot.Series.PathBuilder {
  return (u, seriesIdx, idx0, idx1) => {
    const times = u.data[0] as readonly number[];
    const values = u.data[seriesIdx] as readonly (number | null | undefined)[];
    const scale = u.series[seriesIdx]!.scale ?? RIGHT;
    const shape = geometry(times);
    const stroke = new Path2D();
    const present = (index: number): boolean => {
      const value = values[index];
      return value !== null && value !== undefined && Number.isFinite(value);
    };
    let open = false;
    for (let index = idx0; index <= idx1; index += 1) {
      if (!present(index)) {
        open = false;
        continue;
      }
      const start = times[index]!;
      const end = spanEnd(times, index, shape);
      const x = u.valToPos((start + end) / 2, "x", true);
      const y = u.valToPos(values[index]!, scale, true);
      /* Ein Wert ohne Nachbarn wäre als Linie unsichtbar — er bekommt einen
         Strich über die Mitte seines Abschnitts, so breit wie der halbe Abschnitt. */
      if (!open && !(index < idx1 && present(index + 1))) {
        const quarter = (u.valToPos(end, "x", true) - u.valToPos(start, "x", true)) / 4;
        stroke.moveTo(x - quarter, y);
        stroke.lineTo(x + quarter, y);
        continue;
      }
      if (open) stroke.lineTo(x, y);
      else stroke.moveTo(x, y);
      open = true;
    }
    return { stroke, fill: null, clip: null, band: null, flags: 0 };
  };
}

/**
 * Balken, die ihren Abschnitt füllen (HM-099).
 *
 * Die eingebauten Balken von uPlot stehen **mittig** auf ihrem Zeitpunkt und
 * sind alle gleich breit — so breit wie der kleinste Abstand zweier Punkte.
 * Beides ist für einen Bucket falsch: Sein Zeitpunkt ist sein *Beginn*, und
 * ein Februar ist kürzer als ein März. Jeder Balken reicht deshalb vom Beginn
 * seines Abschnitts bis zu dessen Ende (`spanEnd`), abzüglich der Luft zum
 * Nachbarn — ein Achtel der Breite, mindestens zwei und höchstens vierundzwanzig
 * Bildpunkte, damit sieben Tagesbalken nicht zu einer Fläche verschmelzen und
 * 96 Viertelstunden nicht zu Strichen werden.
 *
 * Die Fuge zwischen zwei Segmenten eines Stapels ist ein Strich in Grundfarbe
 * (`series.width`) **an der Kante, die vom Nullpunkt wegzeigt** — und nur dort
 * (HM-104). Er lag vorher um das ganze Rechteck und nahm jedem Balken oben und
 * unten je zwei Bildpunkte, ob er einen Nachbarn hatte oder nicht; alles unter
 * rund drei Prozent der Spitze war damit unsichtbar, und die Grundlast eines
 * Haushalts liegt genau dort. Die Rechnung dazu steht in `bars.ts`.
 *
 * Zur Seite trennt die Luft zwischen den Abschnitten — mindestens zwei
 * Bildpunkte, damit 96 Viertelstunden nicht zu einer Fläche verschmelzen.
 */
function spanBars(
  geometry: (times: readonly (number | null)[]) => {
    readonly bucket: ChartBucket | null;
    readonly step: number;
    readonly timeZone: string;
  },
  placement: {
    /** Welche Spur im Abschnitt diese Reihe füllt — bei einem Stapel gibt es nur eine. */
    readonly lane: number;
    readonly lanes: number;
    /** Ob über dieser Reihe ein weiteres Segment liegt, von dem eine Fuge sie trennt. */
    readonly divider: boolean;
    /** Die Abdeckung je Zeitpunkt; `null` heißt vollständig (HM-105). */
    readonly coverage: readonly (number | null)[];
    /** Nimmt den Pfad der Balken mit Teilsumme entgegen — für den Schleier danach. */
    readonly partial: (path: Path2D) => void;
    /** Nimmt die Rechtecke je Index entgegen — für das Zurücktreten beim Überfahren. */
    readonly rects: (rects: Map<number, readonly [number, number, number, number]>) => void;
    /** Ob die vom Nullpunkt abgewandte Kante gerundet wird — beim obersten Segment. */
    readonly cap: boolean;
  },
): uPlot.Series.PathBuilder {
  return (u, seriesIdx, idx0, idx1) => {
    const times = u.data[0] as readonly number[];
    const values = u.data[seriesIdx] as readonly (number | null | undefined)[];
    const series = u.series[seriesIdx]!;
    const scale = series.scale ?? "y";
    const shape = geometry(times);
    const ratio = uPlot.pxRatio;
    const right = u.bbox.left + u.bbox.width;

    const x = (at: number): number => u.valToPos(at, "x", true);
    const y = (value: number): number => u.valToPos(value, scale, true);
    const baseline = y(0);
    const fill = new Path2D();
    const stroke = new Path2D();
    const veil = new Path2D();
    const rects = new Map<number, readonly [number, number, number, number]>();

    for (let index = idx0; index <= idx1; index += 1) {
      const value = values[index];
      if (value === null || value === undefined || !Number.isFinite(value)) continue;

      const x0 = x(times[index]!);
      const x1 = Math.min(x(spanEnd(times, index, shape)), right);
      const span = x1 - x0;
      if (span <= 0) continue;

      /* Die Gruppe füllt 62 % des Abschnitts, wie im Entwurf — auf einem
         schmalen Abschnitt (Telefon) 80 %, denn dort ist jeder Bildpunkt
         Balken wert; mindestens zwei Bildpunkte Luft, damit 96 Viertelstunden
         nicht zu einer Fläche werden. */
      const filled = span / ratio < NARROW_SPAN_PX ? 0.8 : 0.62;
      const gap = Math.max(2 * ratio, span * (1 - filled));
      // Die Spur dieser Reihe innerhalb der Luft: nebeneinander, mit einem
      // Bildpunkt Abstand, damit zwei Farben nicht zu einer verschwimmen.
      const lane = (span - gap) / placement.lanes;
      const laneGap = placement.lanes > 1 && lane >= 4 * ratio ? ratio : 0;
      const left = Math.round(x0 + gap / 2 + lane * placement.lane + laneGap / 2);
      const width = Math.max(
        ratio,
        Math.round(x0 + gap / 2 + lane * (placement.lane + 1) - laneGap / 2) - left,
      );

      /* Kein zusätzlicher Rand zur Seite: Die Luft zwischen den Abschnitten
         (`gap`) und zwischen den Spuren (`laneGap`) trennt schon. Ein Rand von
         zwei Bildpunkten je Seite fiel bei schmalen Balken weg und griff erst
         bei breiteren — ein zusammengefasster Zweistundenbalken war damit
         schmaler als der Stundenbalken davor. */
      const extent = barExtent(y(value), baseline, ratio);
      if (extent.height === 0) continue;

      const barLeft = left;
      const barWidth = width;
      const radius = placement.cap ? Math.min(4 * ratio, barWidth / 2, extent.height) : 0;
      roundedBar(fill, barLeft, extent.top, barWidth, extent.height, radius, value >= 0);
      rects.set(index, [barLeft, extent.top, barWidth, extent.height]);
      if (placement.coverage[index] !== null && placement.coverage[index] !== undefined) {
        roundedBar(veil, barLeft, extent.top, barWidth, extent.height, radius, value >= 0);
      }
      if (placement.divider) {
        stroke.moveTo(left, extent.edge);
        stroke.lineTo(left + width, extent.edge);
      }
    }

    placement.partial(veil);
    placement.rects(rects);
    return { stroke, fill, clip: null, band: null, flags: 0 };
  };
}

/**
 * Ein Balken mit gerundeter Kappe — die vom Nullpunkt abgewandte Kante (HM-142,
 * Entwurf: 4 Pixel). Die Kante am Nullpunkt bleibt eckig: Dort steht der Balken
 * auf der Achse oder auf dem Segment darunter.
 */
function roundedBar(
  path: Path2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  upward: boolean,
): void {
  if (r <= 0) {
    path.rect(x, y, w, h);
    return;
  }
  if (upward) {
    path.moveTo(x, y + h);
    path.lineTo(x, y + r);
    path.quadraticCurveTo(x, y, x + r, y);
    path.lineTo(x + w - r, y);
    path.quadraticCurveTo(x + w, y, x + w, y + r);
    path.lineTo(x + w, y + h);
  } else {
    path.moveTo(x, y);
    path.lineTo(x + w, y);
    path.lineTo(x + w, y + h - r);
    path.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    path.lineTo(x + r, y + h);
    path.quadraticCurveTo(x, y + h, x, y + h - r);
  }
  path.closePath();
}
